/**
 * @file 信息投影（projectStateForSeat）。
 *
 * 下行快照的唯一生产者。server-authoritative 信息隔离在类型层落地：
 * 返回的 `SeatView` 在结构上就没有 wall / rng / 他家手牌 / 他家听口 /
 * 暗杠牌面等敏感字段（见 `view.ts` 与 `FORBIDDEN_PUBLIC_FIELDS`）。
 *
 * 平台无关、纯函数，不引用任何 DOM / Node / Workers 全局。
 */

import type {
  GameState,
  SeatIndex,
  Meld,
  Seats,
  PlayerState,
} from './types';
import type {
  SelfView,
  OpponentView,
  SeatView,
  ProjectedMeld,
} from './view';

/**
 * 将他家视角下的副露投影脱敏（D-14）：
 * - 暗杠对他家隐藏牌面（`kind` 置 `null`、`tiles` 置 `[]`）；
 * - 其余副露原样保留（牌面与实例 id 对他家公开）。
 */
function projectMeldForOpponent(m: Meld): ProjectedMeld {
  if (m.type === 'gang_an') {
    return {
      type: m.type,
      kind: null,
      tiles: [],
      fromSeat: m.fromSeat,
      claimedTileId: m.claimedTileId,
      atTurn: m.atTurn,
    };
  }
  return {
    type: m.type,
    kind: m.kind,
    tiles: m.tiles,
    fromSeat: m.fromSeat,
    claimedTileId: m.claimedTileId,
    atTurn: m.atTurn,
  };
}

function toSelfView(p: PlayerState): SelfView {
  return {
    seat: p.seat,
    playerId: p.playerId,
    hand: p.hand,
    melds: p.melds,
    discards: p.discards,
    voidSuit: p.voidSuit,
    isTing: p.isTing,
    tingCandidates: p.tingCandidates,
    hasWon: p.hasWon,
    winRecords: p.winRecords,
    score: p.score,
    connected: p.connected,
    isRobot: p.isRobot,
  };
}

function toOpponentView(p: PlayerState): OpponentView {
  return {
    seat: p.seat,
    playerId: p.playerId,
    // 手牌**张数**，绝不透露内容
    handCount: p.hand.length,
    // 副露公开，暗杠按 D-14 脱敏
    melds: p.melds.map(projectMeldForOpponent),
    discards: p.discards,
    voidSuit: p.voidSuit,
    // D-13：他家听牌态恒 false（M1 不公开他家听牌）
    isTing: false,
    hasWon: p.hasWon,
    winRecords: p.winRecords,
    score: p.score,
    connected: p.connected,
    isRobot: p.isRobot,
  };
}

/**
 * 把权威状态投影成某座位可见的视图。
 *
 * 敏感字段剥离清单（D-12 / D-13 / D-14）：
 * - wall / rng / seed / wallCursor：绝不出现（仅 `wallRemaining` 计数公开）；
 * - 他家 hand：以 `handCount` 替代；
 * - 他家 isTing / tingCandidates：恒 `false` / 不暴露（OpponentView 无该字段）；
 * - 他家暗杠牌面：隐藏（`projectMeldForOpponent`）。
 */
export function projectStateForSeat(state: GameState, seat: SeatIndex): SeatView {
  const viewer = state.players[seat];
  const opponents: OpponentView[] = state.players
    .filter((p) => p.seat !== seat)
    .map(toOpponentView);

  const scores: Seats<number> = [
    state.players[0].score,
    state.players[1].score,
    state.players[2].score,
    state.players[3].score,
  ];

  return {
    gameId: state.gameId,
    seq: state.seq,
    phase: state.phase,
    rules: state.rules,
    viewerSeat: seat,
    self: toSelfView(viewer),
    opponents,
    dealer: state.dealer,
    turn: state.turn,
    // 仅公开剩余张数，牌墙顺序永不下发
    wallRemaining: state.wall.length,
    swapDirection: state.swapDirection,
    lastDiscard: state.lastDiscard,
    turnCount: state.turnCount,
    deadlineAt: state.deadlineAt,
    winnersInOrder: state.winnersInOrder,
    settlement: state.settlement,
    // 动作提示由 getLegalActions 负责，本函数只做信息投影
    actionHint: null,
    scores,
  };
}
